import { Box, CircularProgress, ClickAwayListener, Drawer, IconButton, InputAdornment, Snackbar, TextField, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import ShareIcon from '@mui/icons-material/Share';
import { FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useLocation, useNavigate } from 'react-router';
import { useReactToPrint } from 'react-to-print';
import remarkGfm from 'remark-gfm';
import { knowledgeBaseApi } from 'src/api/mindtime/knowledgeBaseApi';
import {
  applyKbHeadingIdsToDom,
  buildKbArticleHref,
  buildKbTocTree,
  convertMarkdownHeadingsInHtml,
  extractKbHeadings,
  fixKbHtmlImageSrcs,
  generateKbSlug,
  getKbNodeText,
  injectKbHeadingIds,
  isKbHtmlContent,
  KB_STORAGE_KEY_LAST_PAGE,
  normalizeKbArticlePath,
  parseKbArticlePathFromPathname,
  readKbExpandedFolders,
  resolveKbImageSrc,
  writeKbExpandedFolders,
} from 'src/mindtime/components/support/knowledge-base/knowledgeBaseHelpers';
import type {
  KnowledgeBasePageContent,
  KnowledgeBaseSearchResult,
  KnowledgeBaseTocHeading,
  KnowledgeBaseTreeNode,
} from 'src/types/mindtime/knowledgeBase';
import logger from 'src/utils/logger';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    height: '100%',
    minHeight: 0,
    backgroundColor: '#fff',
    border: '1px solid rgba(0, 0, 0, 0.12)',
    borderRadius: 8,
    overflow: 'hidden',
    alignItems: 'stretch',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      height: 'auto',
      minHeight: '65vh',
    },
  },
  sidebar: {
    width: 280,
    minWidth: 280,
    borderRight: '1px solid rgba(0, 0, 0, 0.12)',
    padding: theme.spacing(2),
    overflowY: 'auto',
    height: '100%',
    flexShrink: 0,
    [theme.breakpoints.down('md')]: {
      width: 250,
      minWidth: 250,
    },
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  sidebarDrawer: {
    width: 280,
    padding: theme.spacing(2),
    overflowY: 'auto',
  },
  mobileHeader: {
    display: 'none',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
    },
  },
  searchField: {
    marginBottom: theme.spacing(2),
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      backgroundColor: '#f5f5f5',
      '& fieldset': { border: 'none' },
    },
    '& .MuiOutlinedInput-input': {
      padding: '10px 14px',
      fontSize: 14,
    },
  },
  searchWrapper: {
    position: 'relative',
    marginBottom: theme.spacing(2),
  },
  searchDropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    borderRadius: 8,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    zIndex: 1000,
    maxHeight: 350,
    overflowY: 'auto',
    marginTop: 4,
    border: '1px solid #e0e0e0',
  },
  searchResultItem: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: theme.spacing(1.5, 2),
    cursor: 'pointer',
    borderBottom: '1px solid #f0f0f0',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(0, 102, 153, 0.08)',
    },
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  searchResultIcon: {
    marginRight: theme.spacing(1.5),
    marginTop: 2,
    color: theme.palette.primary.main,
    fontSize: 20,
  },
  searchResultContent: {
    flex: 1,
    minWidth: 0,
  },
  searchResultTitle: {
    fontSize: 14,
    fontWeight: 500,
    color: '#333',
    marginBottom: 2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  searchResultDescription: {
    fontSize: 12,
    color: '#666',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  searchResultPath: {
    fontSize: 11,
    color: '#999',
    marginTop: 2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  searchNoResults: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: '#999',
    fontSize: 14,
  },
  searchLoading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(2),
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 600,
    color: theme.palette.primary.main,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: theme.spacing(1.5),
  },
  treeContainer: {
    marginTop: theme.spacing(1),
  },
  treeItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 8px',
    cursor: 'pointer',
    borderRadius: 4,
    marginBottom: 2,
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  treeItemSelected: {
    backgroundColor: 'rgba(0, 102, 153, 0.1)',
    '&:hover': {
      backgroundColor: 'rgba(0, 102, 153, 0.15)',
    },
  },
  treeItemIcon: {
    marginRight: theme.spacing(1),
    fontSize: 18,
  },
  expandIcon: {
    fontSize: 16,
    marginRight: 4,
    color: theme.palette.primary.main,
    transition: 'transform 0.2s ease',
  },
  expandIconExpanded: {
    transform: 'rotate(90deg)',
  },
  folderIcon: {
    color: theme.palette.primary.main,
  },
  fileIcon: {
    color: '#008C3C',
  },
  fileIconSelected: {
    color: theme.palette.primary.main,
  },
  treeItemText: {
    fontSize: 14,
    color: '#333',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  treeItemTextSelected: {
    color: theme.palette.primary.main,
    fontWeight: 500,
  },
  treeItemChildren: {
    marginLeft: '2px',
    borderLeft: '1px solid #ccc',
    paddingLeft: theme.spacing(1),
  },
  loadingIcon: {
    marginLeft: 'auto',
  },
  contentArea: {
    flex: 1,
    display: 'flex',
    overflow: 'hidden',
    alignItems: 'stretch',
    minWidth: 0,
    minHeight: 0,
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  pageContents: {
    width: 220,
    minWidth: 220,
    borderRight: '1px solid rgba(0, 0, 0, 0.12)',
    padding: theme.spacing(2),
    overflowY: 'auto',
    height: '100%',
    flexShrink: 0,
    [theme.breakpoints.down('md')]: {
      width: '100%',
      minWidth: '100%',
      height: 'auto',
      maxHeight: 200,
      borderRight: 'none',
      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    },
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  pageContentsTitle: {
    fontSize: 11,
    fontWeight: 600,
    color: '#999',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: theme.spacing(1.5),
  },
  contentHeading: {
    fontSize: 14,
    color: '#666',
    padding: '4px 0',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'flex-start',
    transition: 'color 0.2s ease',
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  contentHeadingActive: {
    color: theme.palette.primary.main,
    fontWeight: 500,
  },
  contentHeadingNested: {
    marginLeft: theme.spacing(0),
    fontSize: 14,
  },
  contentHeadingLevel3: {
    marginLeft: theme.spacing(2),
    fontSize: 14,
  },
  mainContentWrapper: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minWidth: 0,
    minHeight: 0,
  },
  mainContentHeader: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
    flexShrink: 0,
  },
  headerButton: {
    color: theme.palette.primary.main,
    marginLeft: theme.spacing(1),
  },
  mainContent: {
    flex: 1,
    minHeight: 0,
    padding: theme.spacing(4),
    overflowY: 'auto',
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(3),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4),
  },
  noFileSelected: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    color: '#999',
  },
  noFileIcon: {
    fontSize: 80,
    color: '#ccc',
    marginBottom: theme.spacing(2),
  },
  noFileTitle: {
    fontSize: 18,
    fontWeight: 500,
    color: '#666',
    marginBottom: theme.spacing(1),
  },
  noFileSubtitle: {
    fontSize: 14,
    color: '#999',
  },
  pageTitle: {
    fontSize: 28,
    fontWeight: 600,
    color: '#333',
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      fontSize: 24,
    },
  },
  pageDescription: {
    fontSize: 14,
    color: '#888',
    marginBottom: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    borderBottom: '1px solid #eee',
  },
  markdownContent: {
    '& h1': {
      fontSize: 32,
      fontWeight: 600,
      color: theme.palette.primary.main,
      marginBottom: theme.spacing(2),
      marginTop: theme.spacing(3),
      scrollMarginTop: '16px',
    },
    '& h2': {
      fontSize: 24,
      fontWeight: 600,
      color: '#333',
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(2),
      scrollMarginTop: '16px',
    },
    '& h3': {
      fontSize: 18,
      fontWeight: 500,
      color: '#555',
      marginBottom: theme.spacing(1),
      marginTop: theme.spacing(3),
      scrollMarginTop: '16px',
    },
    '& h4': {
      fontSize: 16,
      fontWeight: 500,
      color: '#666',
      marginBottom: theme.spacing(1),
      marginTop: theme.spacing(2),
      scrollMarginTop: '16px',
    },
    '& p': {
      fontSize: 14,
      lineHeight: 1.7,
      color: '#666',
      marginBottom: theme.spacing(2),
    },
    '& ul, & ol': {
      paddingLeft: theme.spacing(3),
      marginBottom: theme.spacing(2),
    },
    '& li': {
      fontSize: 14,
      lineHeight: 1.7,
      color: '#666',
      marginBottom: theme.spacing(0.5),
    },
    '& a': {
      color: theme.palette.primary.main,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    '& code': {
      backgroundColor: '#f5f5f5',
      padding: '2px 6px',
      borderRadius: 4,
      fontSize: 13,
      fontFamily: 'monospace',
    },
    '& pre': {
      backgroundColor: '#f5f5f5',
      padding: theme.spacing(2),
      borderRadius: 8,
      overflow: 'auto',
      '& code': {
        backgroundColor: 'transparent',
        padding: 0,
      },
    },
    '& blockquote': {
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      margin: theme.spacing(2, 0),
      paddingLeft: theme.spacing(2),
      color: '#666',
      fontStyle: 'italic',
    },
    '& table': {
      width: '100%',
      borderCollapse: 'collapse',
      marginBottom: theme.spacing(2),
    },
    '& th, & td': {
      border: '1px solid #e0e0e0',
      padding: theme.spacing(1, 2),
      textAlign: 'left',
    },
    '& th': {
      backgroundColor: '#f5f5f5',
      fontWeight: 600,
    },
    '& img': {
      maxWidth: '100% !important',
      height: 'auto',
      borderRadius: 8,
    },
  },
}));

const findNodeById = (
  nodes: KnowledgeBaseTreeNode[],
  id: number
): KnowledgeBaseTreeNode | null =>
  nodes.reduce<KnowledgeBaseTreeNode | null>((found, node) => {
    if (found) return found;
    if (node.id === id) return node;
    if (node.children?.length) return findNodeById(node.children, id);
    return null;
  }, null);

const findNodeByPageId = (
  nodes: KnowledgeBaseTreeNode[],
  pageId: number
): KnowledgeBaseTreeNode | null =>
  nodes.reduce<KnowledgeBaseTreeNode | null>((found, node) => {
    if (found) return found;
    if (node.pageId === pageId) return node;
    if (node.children?.length) return findNodeByPageId(node.children, pageId);
    return null;
  }, null);

const findNodeByPath = (
  nodes: KnowledgeBaseTreeNode[],
  path: string
): KnowledgeBaseTreeNode | null => {
  const normalized = normalizeKbArticlePath(path);
  if (!normalized) return null;

  return nodes.reduce<KnowledgeBaseTreeNode | null>((found, node) => {
    if (found) return found;
    if (normalizeKbArticlePath(node.path) === normalized) return node;
    if (node.children?.length) return findNodeByPath(node.children, normalized);
    return null;
  }, null);
};

const collectAncestorIds = (
  nodes: KnowledgeBaseTreeNode[],
  targetId: number,
  trail: number[] = []
): number[] | null =>
  nodes.reduce<number[] | null>((found, node) => {
    if (found) return found;
    if (node.id === targetId) return trail;
    if (node.children?.length) {
      return collectAncestorIds(node.children, targetId, [...trail, node.id]);
    }
    return null;
  }, null);

/** Expand only folders whose path is a prefix of the target page path. */
const collectAncestorIdsByPath = (
  nodes: KnowledgeBaseTreeNode[],
  targetPath: string
): number[] => {
  const normalized = targetPath.replace(/^\/+|\/+$/g, '');
  if (!normalized) return [];

  return nodes.reduce<number[]>((ids, node) => {
    if (!node.isFolder || !node.path) return ids;
    const folderPath = node.path.replace(/^\/+|\/+$/g, '');
    if (normalized === folderPath || normalized.startsWith(`${folderPath}/`)) {
      ids.push(node.id);
      if (node.children?.length) {
        ids.push(...collectAncestorIdsByPath(node.children, normalized));
      }
    }
    return ids;
  }, []);
};

const updateNodeChildrenRecursive = (
  nodes: KnowledgeBaseTreeNode[],
  targetId: number,
  children: KnowledgeBaseTreeNode[]
): KnowledgeBaseTreeNode[] =>
  nodes.map((node) => {
    if (node.id === targetId) {
      return { ...node, children, isLoading: false };
    }
    if (node.children?.length) {
      return {
        ...node,
        children: updateNodeChildrenRecursive(node.children, targetId, children),
      };
    }
    return node;
  });

const resolveArticleAlongPath = async (
  rootTree: KnowledgeBaseTreeNode[],
  articlePath: string,
  loadChildren: (
    current: KnowledgeBaseTreeNode[],
    folderId: number
  ) => Promise<KnowledgeBaseTreeNode[]>,
  segmentIndex = 0,
  expandedIds: number[] = []
): Promise<{
  tree: KnowledgeBaseTreeNode[];
  node: KnowledgeBaseTreeNode | null;
  expandedIds: number[];
}> => {
  const normalized = normalizeKbArticlePath(articlePath);
  const segments = normalized.split('/').filter(Boolean);

  if (!segments.length || segmentIndex >= segments.length) {
    return {
      tree: rootTree,
      node: findNodeByPath(rootTree, normalized),
      expandedIds,
    };
  }

  const prefix = segments.slice(0, segmentIndex + 1).join('/');
  const node = findNodeByPath(rootTree, prefix);

  if (!node) {
    return {
      tree: rootTree,
      node: findNodeByPath(rootTree, normalized),
      expandedIds,
    };
  }

  let workingTree = rootTree;
  const nextExpanded = [...expandedIds];

  if (node.isFolder) {
    nextExpanded.push(node.id);
    workingTree = await loadChildren(workingTree, node.id);
  }

  if (segmentIndex === segments.length - 1 && node.pageId) {
    return { tree: workingTree, node, expandedIds: nextExpanded };
  }

  return resolveArticleAlongPath(
    workingTree,
    normalized,
    loadChildren,
    segmentIndex + 1,
    nextExpanded
  );
};

type LoadFolderChildrenFn = (
  currentNodes: KnowledgeBaseTreeNode[],
  targetFolderId: number
) => Promise<KnowledgeBaseTreeNode[]>;

const KnowledgeBaseBrowser: FC = () => {
  const classes = useStyles();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const printRef = useRef<HTMLDivElement>(null);
  const mainContentRef = useRef<HTMLDivElement>(null);
  const hasRestoredRef = useRef(false);
  const skipUrlSyncRef = useRef(false);
  const treeDataRef = useRef<KnowledgeBaseTreeNode[]>([]);
  const loadFolderChildrenRef = useRef<LoadFolderChildrenFn | null>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRequestIdRef = useRef(0);

  const [treeData, setTreeData] = useState<KnowledgeBaseTreeNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<KnowledgeBaseSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [selectedItem, setSelectedItem] = useState<KnowledgeBaseTreeNode | null>(null);
  const [expandedFolders, setExpandedFolders] = useState<number[]>(() => readKbExpandedFolders());
  const [loadingFolders, setLoadingFolders] = useState<number[]>([]);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [pageContent, setPageContent] = useState<KnowledgeBasePageContent | null>(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [activeHeadingId, setActiveHeadingId] = useState('');
  const [tocExpanded, setTocExpanded] = useState<Record<string, boolean>>({});

  treeDataRef.current = treeData;

  const selectedPathRef = useRef('');
  selectedPathRef.current = normalizeKbArticlePath(selectedItem?.path);
  const initialUrlPathRef = useRef(parseKbArticlePathFromPathname(location.pathname));

  const tableOfContents = useMemo(() => {
    if (!pageContent?.content) return [];
    return buildKbTocTree(extractKbHeadings(pageContent.content));
  }, [pageContent?.content]);

  const flatHeadings = useMemo(() => {
    if (!pageContent?.content) return [];
    return extractKbHeadings(pageContent.content);
  }, [pageContent?.content]);

  // Selection → URL (user/sidebar/search). Skip the next URL→selection sync.
  useEffect(() => {
    if (!selectedItem?.pageId) return;

    localStorage.setItem(KB_STORAGE_KEY_LAST_PAGE, JSON.stringify(selectedItem));

    const articlePath = normalizeKbArticlePath(selectedItem.path);
    if (!articlePath) return;

    const nextHref = buildKbArticleHref(articlePath);
    if (window.location.pathname === nextHref) return;

    skipUrlSyncRef.current = true;
    navigate(nextHref, { replace: true });
  }, [selectedItem, navigate]);

  useEffect(() => {
    writeKbExpandedFolders(expandedFolders);
  }, [expandedFolders]);

  useEffect(
    () => () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    },
    []
  );

  const fetchFolderContents = useCallback(async (id: string = '1') => {
    try {
      setLoading(true);
      setLoadError(null);
      const response = await knowledgeBaseApi.getFolderContentsById(id);
      if (response?.data?.pages?.tree) {
        setTreeData(response.data.pages.tree);
      } else {
        setLoadError('Knowledge base returned an empty response.');
      }
    } catch (err: any) {
      logger(err, 'error');
      if (err?.response?.status === 401) {
        setLoadError(
          'Unable to access the knowledge base. Your login token may not be valid for this service.'
        );
      } else {
        setLoadError('Unable to load the knowledge base. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPageContent = useCallback(async (pageId: number) => {
    try {
      setLoadingContent(true);
      const response = await knowledgeBaseApi.getPageById(String(pageId));
      if (response?.data?.pages?.single) {
        setPageContent(response.data.pages.single);
      } else {
        setPageContent(null);
      }
    } catch (err) {
      logger(err, 'error');
      setPageContent(null);
    } finally {
      setLoadingContent(false);
    }
  }, []);

  const loadFolderChildren = useCallback(
    async (
      nodes: KnowledgeBaseTreeNode[],
      folderId: number
    ): Promise<KnowledgeBaseTreeNode[]> => {
      const existing = findNodeById(nodes, folderId);
      if (existing?.children?.length) return nodes;

      try {
        setLoadingFolders((prev) =>
          prev.includes(folderId) ? prev : [...prev, folderId]
        );
        const response = await knowledgeBaseApi.getFolderContentsById(String(folderId));
        const children = response?.data?.pages?.tree ?? [];
        return updateNodeChildrenRecursive(nodes, folderId, children);
      } catch (err) {
        logger(err, 'error');
        return nodes;
      } finally {
        setLoadingFolders((prev) => prev.filter((id) => id !== folderId));
      }
    },
    []
  );

  loadFolderChildrenRef.current = loadFolderChildren;

  useEffect(() => {
    fetchFolderContents('0');
  }, [fetchFolderContents]);

  // One-time restore from URL (preferred) or last opened article.
  useEffect(() => {
    if (!treeData.length || hasRestoredRef.current) {
      return undefined;
    }

    let cancelled = false;

    const restoreSelection = async () => {
      const urlPath = initialUrlPathRef.current;
      let savedPage: KnowledgeBaseTreeNode | null = null;

      if (!urlPath) {
        const savedRaw = localStorage.getItem(KB_STORAGE_KEY_LAST_PAGE);
        if (savedRaw) {
          try {
            savedPage = JSON.parse(savedRaw) as KnowledgeBaseTreeNode;
          } catch (err) {
            logger(err, 'error');
          }
        }
      }

      const pathToRestore = urlPath || normalizeKbArticlePath(savedPage?.path);
      let nextTree = treeData;
      let nodeToSelect: KnowledgeBaseTreeNode | null = null;
      let expandedIds: number[] = [];

      if (pathToRestore) {
        const resolved = await resolveArticleAlongPath(
          treeData,
          pathToRestore,
          loadFolderChildren
        );
        if (cancelled) return;
        nextTree = resolved.tree;
        nodeToSelect = resolved.node;
        expandedIds = resolved.expandedIds;
      }

      if (!nodeToSelect && savedPage?.pageId) {
        nodeToSelect =
          findNodeById(nextTree, savedPage.id) ||
          findNodeByPageId(nextTree, savedPage.pageId) ||
          savedPage;
        expandedIds =
          collectAncestorIds(nextTree, nodeToSelect.id) ??
          (savedPage.parent ? [savedPage.parent] : []);
      }

      if (cancelled) return;

      hasRestoredRef.current = true;

      if (nextTree !== treeData) {
        setTreeData(nextTree);
      }

      if (expandedIds.length) {
        setExpandedFolders(Array.from(new Set(expandedIds)));
      }

      if (nodeToSelect?.pageId) {
        if (urlPath && normalizeKbArticlePath(nodeToSelect.path) === urlPath) {
          skipUrlSyncRef.current = true;
        }
        setSelectedItem(nodeToSelect);
      }
    };

    restoreSelection();

    return () => {
      cancelled = true;
    };
  }, [treeData, loadFolderChildren]);

  // URL → selection for back/forward / pasted links only (not sidebar clicks).
  useEffect(() => {
    if (!hasRestoredRef.current) return undefined;

    if (skipUrlSyncRef.current) {
      skipUrlSyncRef.current = false;
      return undefined;
    }

    const urlPath = parseKbArticlePathFromPathname(location.pathname);
    if (!urlPath) return undefined;
    if (selectedPathRef.current === urlPath) return undefined;

    let cancelled = false;

    const syncFromUrl = async () => {
      const loadChildren = loadFolderChildrenRef.current;
      if (!loadChildren) return;

      const resolved = await resolveArticleAlongPath(
        treeDataRef.current,
        urlPath,
        loadChildren
      );
      if (cancelled || !resolved.node?.pageId) return;

      if (parseKbArticlePathFromPathname(window.location.pathname) !== urlPath) {
        return;
      }

      setTreeData(resolved.tree);
      setExpandedFolders(Array.from(new Set(resolved.expandedIds)));
      skipUrlSyncRef.current = true;
      setSelectedItem(resolved.node);
    };

    syncFromUrl();

    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  useEffect(() => {
    if (selectedItem?.pageId) {
      fetchPageContent(selectedItem.pageId);
    } else {
      setPageContent(null);
    }
  }, [selectedItem, fetchPageContent]);

  // Stamp TOC ids onto rendered headings so Page Contents clicks resolve.
  useLayoutEffect(() => {
    if (loadingContent || !pageContent?.content || flatHeadings.length === 0) {
      return undefined;
    }

    const container = mainContentRef.current;
    if (!container) return undefined;

    applyKbHeadingIdsToDom(container, flatHeadings);
    return undefined;
  }, [loadingContent, pageContent?.content, flatHeadings]);

  useEffect(() => {
    const container = mainContentRef.current;
    if (!container || flatHeadings.length === 0 || loadingContent) {
      return undefined;
    }

    const updateActiveHeading = () => {
      // Activation line = top of the article pane (already below share/print bar)
      // + a small inset so the heading under the bar edge wins, not one still hidden above it.
      const activationLine = container.getBoundingClientRect().top + 12;

      let nextActiveId = flatHeadings[0]?.id ?? '';

      flatHeadings.forEach((heading) => {
        if (!heading.id) return;
        const element = container.querySelector(
          `#${CSS.escape(heading.id)}`
        ) as HTMLElement | null;
        if (!element) return;

        // Last heading whose top has reached/passed the visible top of the article pane.
        if (element.getBoundingClientRect().top <= activationLine) {
          nextActiveId = heading.id;
        }
      });

      setActiveHeadingId((prev) => (prev === nextActiveId ? prev : nextActiveId));
    };

    updateActiveHeading();
    container.addEventListener('scroll', updateActiveHeading, { passive: true });
    return () => container.removeEventListener('scroll', updateActiveHeading);
  }, [flatHeadings, loadingContent, pageContent?.content]);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: pageContent?.title || 'Knowledge Base',
  });

  const handleShare = async () => {
    const articlePath =
      normalizeKbArticlePath(selectedItem?.path) ||
      normalizeKbArticlePath(pageContent?.path);
    const shareUrl = `${window.location.origin}${buildKbArticleHref(articlePath)}`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: pageContent?.title || 'Knowledge Base',
          url: shareUrl,
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        setSnackbarMessage('URL copied to clipboard');
        setSnackbarOpen(true);
      }
    } catch (err) {
      logger(err, 'error');
      setSnackbarMessage('Failed to copy URL');
      setSnackbarOpen(true);
    }
  };

  const searchPages = useCallback(async (query: string) => {
    const trimmed = query.trim();
    if (!trimmed) {
      setSearchResults([]);
      setSearchLoading(false);
      setShowSearchDropdown(false);
      return;
    }

    const requestId = searchRequestIdRef.current + 1;
    searchRequestIdRef.current = requestId;

    try {
      setSearchLoading(true);
      setShowSearchDropdown(true);
      const response = await knowledgeBaseApi.searchPages(trimmed);
      if (requestId !== searchRequestIdRef.current) return;

      const results = response?.data?.pages?.search?.results ?? [];
      setSearchResults(results);
    } catch (err) {
      if (requestId !== searchRequestIdRef.current) return;
      logger(err, 'error');
      setSearchResults([]);
    } finally {
      if (requestId === searchRequestIdRef.current) {
        setSearchLoading(false);
      }
    }
  }, []);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchQuery(query);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!query.trim()) {
      setSearchResults([]);
      setSearchLoading(false);
      setShowSearchDropdown(false);
      return;
    }

    setShowSearchDropdown(true);
    setSearchLoading(true);
    searchTimeoutRef.current = setTimeout(() => {
      searchPages(query);
    }, 300);
  };

  const handleSearchResultClick = (result: KnowledgeBaseSearchResult) => {
    const pageId = Number.parseInt(result.id, 10);
    if (Number.isNaN(pageId)) return;

    const liveNode = findNodeByPageId(treeData, pageId);
    const selectedNode: KnowledgeBaseTreeNode = liveNode ?? {
      id: pageId,
      path: result.path ?? '',
      depth: 0,
      title: result.title,
      isFolder: false,
      pageId,
      parent: 0,
    };

    setSelectedItem(selectedNode);

    // Keep only the path to this result so previous search expansions collapse.
    const ancestorsFromTree = collectAncestorIds(treeData, selectedNode.id);
    const ancestorsFromPath =
      !ancestorsFromTree?.length && result.path
        ? collectAncestorIdsByPath(treeData, result.path)
        : [];
    setExpandedFolders(ancestorsFromTree?.length ? ancestorsFromTree : ancestorsFromPath);

    setSearchQuery('');
    setSearchResults([]);
    setShowSearchDropdown(false);

    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  };

  const handleNodeClick = async (node: KnowledgeBaseTreeNode) => {
    if (node.pageId) {
      setSelectedItem(node);
      if (isMobile) {
        setMobileDrawerOpen(false);
      }
    }

    if (!node.isFolder) return;

    const isExpanded = expandedFolders.includes(node.id);

    if (isExpanded) {
      setExpandedFolders((prev) => prev.filter((id) => id !== node.id));
      return;
    }

    setExpandedFolders((prev) => [...prev, node.id]);

    if (node.children?.length) return;

    const nextTree = await loadFolderChildren(treeData, node.id);
    setTreeData(nextTree);
  };

  const handleTocClick = (headingId: string) => {
    const container = mainContentRef.current;
    if (!container || !headingId) return;

    // Re-stamp in case ReactMarkdown remounted headings without ids.
    applyKbHeadingIdsToDom(container, flatHeadings);

    const element = container.querySelector(`#${CSS.escape(headingId)}`) as HTMLElement | null;
    if (!element) return;

    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const nextTop = container.scrollTop + (elementRect.top - containerRect.top) - 16;

    container.scrollTo({ top: Math.max(nextTop, 0), behavior: 'smooth' });
    setActiveHeadingId(headingId);
  };

  const toggleTocExpand = (headingId: string) => {
    setTocExpanded((prev) => ({
      ...prev,
      [headingId]: !prev[headingId],
    }));
  };

  const renderTreeItem = (node: KnowledgeBaseTreeNode) => {
    const isExpanded = expandedFolders.includes(node.id);
    const isSelected = selectedItem?.id === node.id;
    const isLoadingChildren = loadingFolders.includes(node.id);

    return (
      <Box key={node.id}>
        <Box
          className={`${classes.treeItem} ${isSelected ? classes.treeItemSelected : ''}`}
          onClick={(event) => {
            event.stopPropagation();
            handleNodeClick(node);
          }}
        >
          {node.isFolder && (
            <ChevronRightIcon
              className={`${classes.expandIcon} ${isExpanded ? classes.expandIconExpanded : ''}`}
            />
          )}
          {node.isFolder ? (
            <FolderIcon className={`${classes.treeItemIcon} ${classes.folderIcon}`} />
          ) : (
            <InsertDriveFileOutlinedIcon
              className={`${classes.treeItemIcon} ${isSelected ? classes.fileIconSelected : classes.fileIcon
                }`}
            />
          )}
          <Typography
            title={node.title}
            className={`${classes.treeItemText} ${isSelected ? classes.treeItemTextSelected : ''}`}
          >
            {node.title}
          </Typography>
          {isLoadingChildren && (
            <CircularProgress size={14} className={classes.loadingIcon} />
          )}
        </Box>
        {node.isFolder && isExpanded && node.children && node.children.length > 0 && (
          <Box className={classes.treeItemChildren}>
            {node.children.map((child) => renderTreeItem(child))}
          </Box>
        )}
      </Box>
    );
  };

  const renderTocItem = (heading: KnowledgeBaseTocHeading, depth = 0) => {
    const hasChildren = Boolean(heading.children?.length);
    const isExpanded = tocExpanded[heading.id] !== false;
    const isActive = activeHeadingId === heading.id;
    const levelClass =
      depth === 0 ? '' : depth === 1 ? classes.contentHeadingNested : classes.contentHeadingLevel3;

    return (
      <Box key={heading.id}>
        <Typography
          title={heading.text}
          className={`${classes.contentHeading} ${levelClass} ${isActive ? classes.contentHeadingActive : ''
            }`}
          onClick={() => handleTocClick(heading.id)}
        >
          {hasChildren && (
            <Box
              component="span"
              onClick={(event) => {
                event.stopPropagation();
                toggleTocExpand(heading.id);
              }}
              style={{ display: 'inline-flex', cursor: 'pointer' }}
            >
              {isExpanded ? (
                <ExpandLessIcon
                  style={{ fontSize: 16, marginRight: 4, color: theme.palette.primary.main }}
                />
              ) : (
                <ExpandMoreIcon
                  style={{ fontSize: 16, marginRight: 4, color: theme.palette.primary.main }}
                />
              )}
            </Box>
          )}
          {heading.text}
        </Typography>
        {hasChildren &&
          isExpanded &&
          heading.children?.map((child) => renderTocItem(child, depth + 1))}
      </Box>
    );
  };

  const renderSidebarContent = () => (
    <>
      <ClickAwayListener onClickAway={() => setShowSearchDropdown(false)}>
        <Box className={classes.searchWrapper}>
          <TextField
            className={classes.searchField}
            placeholder="Search"
            variant="outlined"
            fullWidth
            size="small"
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={() => {
              if (searchQuery.trim() && (searchResults.length > 0 || searchLoading)) {
                setShowSearchDropdown(true);
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: '#999', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />

          {showSearchDropdown && (
            <Box className={classes.searchDropdown}>
              {searchLoading ? (
                <Box className={classes.searchLoading}>
                  <CircularProgress size={24} />
                </Box>
              ) : searchResults.length > 0 ? (
                searchResults.map((result) => (
                  <Box
                    key={result.id}
                    className={classes.searchResultItem}
                    onClick={() => handleSearchResultClick(result)}
                  >
                    <InsertDriveFileOutlinedIcon className={classes.searchResultIcon} />
                    <Box className={classes.searchResultContent}>
                      <Typography title={result.title} className={classes.searchResultTitle}>
                        {result.title}
                      </Typography>
                      {result.description ? (
                        <Typography
                          title={result.description}
                          className={classes.searchResultDescription}
                        >
                          {result.description}
                        </Typography>
                      ) : null}
                      {result.path ? (
                        <Typography title={result.path} className={classes.searchResultPath}>
                          {result.path}
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                ))
              ) : searchQuery.trim() ? (
                <Typography className={classes.searchNoResults}>No results found</Typography>
              ) : null}
            </Box>
          )}
        </Box>
      </ClickAwayListener>

      <Typography className={classes.sectionTitle}>Knowledge Base</Typography>

      <Box className={classes.treeContainer}>
        {loading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress size={24} />
          </Box>
        ) : loadError ? (
          <Typography className={classes.searchNoResults} style={{ textAlign: 'left' }}>
            {loadError}
          </Typography>
        ) : (
          <Box className={classes.treeItemChildren}>
            {treeData.map((node) => renderTreeItem(node))}
          </Box>
        )}
      </Box>
    </>
  );

  const renderNoFileSelected = () => (
    <Box className={classes.noFileSelected}>
      <DescriptionOutlinedIcon className={classes.noFileIcon} />
      <Typography className={classes.noFileTitle}>No file selected</Typography>
      <Typography className={classes.noFileSubtitle}>
        Select an article from the sidebar to view its contents
      </Typography>
    </Box>
  );

  const renderMainContent = () => {
    if (loadingContent) {
      return (
        <Box className={classes.loadingContainer}>
          <CircularProgress size={32} />
        </Box>
      );
    }

    if (pageContent?.content) {
      const isHtml = isKbHtmlContent(pageContent.content);
      const htmlContent = isHtml
        ? fixKbHtmlImageSrcs(
            injectKbHeadingIds(convertMarkdownHeadingsInHtml(pageContent.content))
          )
        : null;

      return (
        <>
          {pageContent.title && (
            <Typography className={classes.pageTitle}>{pageContent.title}</Typography>
          )}
          {pageContent.description && (
            <Typography className={classes.pageDescription}>{pageContent.description}</Typography>
          )}

          {isHtml && htmlContent ? (
            <Box
              className={classes.markdownContent}
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
          ) : (
            <Box className={classes.markdownContent}>
              <ReactMarkdown
                plugins={[remarkGfm]}
                renderers={{
                  heading: ({ level, children, ...props }) => {
                    const text = getKbNodeText(children);
                    const id = generateKbSlug(text);
                    const Tag = `h${level}` as keyof JSX.IntrinsicElements;
                    return (
                      <Tag id={id} {...props}>
                        {children}
                      </Tag>
                    );
                  },
                  link: ({ href, children, ...props }) => (
                    <a
                      href={href}
                      target={href?.startsWith('http') ? '_blank' : undefined}
                      rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
                      {...props}
                    >
                      {children}
                    </a>
                  ),
                  image: ({ src, alt, ...props }) => (
                    <img
                      src={resolveKbImageSrc(src)}
                      alt={alt || ''}
                      referrerPolicy="no-referrer"
                      style={{ maxWidth: '100%', height: 'auto' }}
                      {...props}
                    />
                  ),
                }}
              >
                {pageContent.content}
              </ReactMarkdown>
            </Box>
          )}
        </>
      );
    }

    return renderNoFileSelected();
  };

  const renderTableOfContents = () => {
    if (!selectedItem || selectedItem.isFolder || !pageContent) return null;

    return (
      <>
        <Typography className={classes.pageContentsTitle}>Page contents</Typography>
        {tableOfContents.length > 0 ? (
          tableOfContents
            .flatMap((heading) =>
              heading.level === 1 && heading.children?.length ? heading.children : heading
            )
            .map((heading) => renderTocItem(heading))
        ) : (
          <Typography style={{ fontSize: 14, color: '#999' }}>No headings found</Typography>
        )}
      </>
    );
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.mobileHeader}>
        <IconButton onClick={() => setMobileDrawerOpen(true)}>
          <MenuIcon />
        </IconButton>
        <Typography variant="h6" style={{ marginLeft: 8 }}>
          Knowledge Base
        </Typography>
      </Box>

      <Drawer anchor="left" open={mobileDrawerOpen} onClose={() => setMobileDrawerOpen(false)}>
        <Box className={classes.sidebarDrawer}>{renderSidebarContent()}</Box>
      </Drawer>

      <Box className={classes.sidebar}>{renderSidebarContent()}</Box>

      <Box className={classes.contentArea}>
        <Box className={classes.pageContents}>{renderTableOfContents()}</Box>

        <Box className={classes.mainContentWrapper}>
          <Box className={classes.mainContentHeader}>
            <Tooltip title="Share">
              <IconButton className={classes.headerButton} onClick={handleShare} size="small">
                <ShareIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Print">
              <IconButton
                className={classes.headerButton}
                onClick={handlePrint}
                size="small"
              >
                <PrintIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <Box
            className={classes.mainContent}
            ref={(element: HTMLDivElement | null) => {
              printRef.current = element;
              mainContentRef.current = element;
            }}
          >
            {renderMainContent()}
          </Box>
        </Box>
      </Box>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default KnowledgeBaseBrowser;
